import React, { useEffect, useMemo } from 'react';
import { Form, Typography, Button, Card, Divider, Alert, Space, message } from 'antd';
import { SaveOutlined, InfoCircleOutlined, LinkOutlined, ApiOutlined, GlobalOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import CommonForm from '../../../global/form';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedHoneybook = ({ destinations, zapierSetup, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();

    const DESTINATION_HONEYBOOK = SOURCES.DESTINATION_HONEYBOOK || "honeybook";

    const { mutate: saveHoneybook, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => {
            message.success("HoneyBook configuration saved successfully");
        }
    });

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_HONEYBOOK);
            if (config) {
                form.setFieldsValue({
                    honeyBookWebhook: config.publicKey,
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0
                });
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        const payload = {
            sendLeadsLimit: values.sendLeadsLimit,
            integrationCompany: DESTINATION_HONEYBOOK,
            locations: [],
            // destinationAccountEmail and password were commented out in legacy, 
            // but I'll keep the structure if needed later
        };
        saveHoneybook(payload);
    };

    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Zapier Webhook",
            name: "honeyBookWebhook",
            placeholder: "Zapier webhook",
            readOnly: true,
            span: 24,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "How many leads to send initially?",
            type: "number",
            span: 24,
        },
    ], []);

    const canSave = zapierSetup && zapierSetup.zapCreatedByAdmin;

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Automate your HoneyBook lead sync via Zapier.</Text>
                </div>
            </div>

            <div className="space-y-6">
                {!zapierSetup ? (
                    <div className="bg-primary/10 p-3 rounded-xl">
                        <div className="flex items-center gap-3">
                            <GlobalOutlined className="text-primary mt-0" />
                            <Text className="text-sm text-primary">
                                <Text strong className="text-primary">NOTE:</Text> To send lead(s) to <Text strong>HoneyBook</Text> we need <Text strong>Zapier</Text> credentials.
                                You can add them <Link to="/zapier-configuration" className="text-primary hover:underline font-bold ml-1">here</Link>.
                            </Text>
                        </div>
                    </div>
                ) : !zapierSetup.zapCreatedByAdmin ? (
                    <div className="bg-primary/10 p-3 rounded-xl">
                        <div className="flex items-center gap-3">
                            <GlobalOutlined className="text-primary mt-0" />
                            <Text className="text-sm text-primary">
                                Your <Text strong className="text-primary">Zapier</Text> setup is not yet completed by our team. You will be notified once done.
                            </Text>
                        </div>
                    </div>
                ) : null}

                <Card className="rounded-2xl border-none shadow-sm transition-all">
                    <CommonForm
                        form={form}
                        data={formData}
                        onFinish={onFinish}
                        layoutType="vertical"
                    />

                    <Divider className="mb-4 border-gray-100" />

                    <div className="flex justify-start">
                        {canSave ? (
                            <Button
                                size="large"
                                icon={isSaving ? null : <SaveOutlined />}
                                onClick={() => form.submit()}
                                className={cn(btnClassName)}
                            >
                                {isSaving && <Spinner className={"!border-white"} />}
                                Save Configuration
                            </Button>
                        ) : (
                            <Button
                                disabled
                                size="large"
                                className="h-12 px-10 rounded-xl bg-gray-100 text-gray-400 border-gray-100"
                            >
                                Setup Incomplete
                            </Button>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default EnhancedHoneybook;
